/**
 * capacitesDuDocument — ce que le fournisseur sait RÉELLEMENT exécuter (#987).
 *
 * > Un verbe ne doit jamais être proposé au modèle si l'opération dont il
 * > dépend n'existe pas. Ce module interroge `capabilities.get()` une fois par
 * > document et rend la réponse à qui décide.
 *
 * ## Le défaut qu'il ferme
 *
 * `undo_changes` appelait `doc.undo`, une méthode que le Document API n'a
 * jamais exposée — l'undo vit sous `doc.history.undo`. Il rendait donc
 * `CAPABILITY_UNAVAILABLE` **à chaque appel depuis sa mise en service**, et
 * rien ne rougissait : le double de test définissait, lui, un `undo` racine.
 *
 * Chaque verbe faisait jusqu'ici sa propre garde `typeof ctx.doc.X !== 'function'`,
 * c'est-à-dire une devinette sur la forme de l'objet. Les capacités, elles,
 * étaient citées comme argument d'autorité dans trois commentaires du module et
 * **jamais interrogées à l'exécution**.
 *
 * ## Pourquoi une lecture réussie par document
 *
 * `capabilities.get()` rend 423 entrées ; l'appeler à chaque action coûterait un
 * aller-retour par verbe pour une réponse qui ne bouge pas tant que le document
 * est monté. Une réponse exploitable est donc mémoïsée dans une `WeakMap` sur
 * l'objet `doc` — même patron que `lireLeCorpsSdm` : un nouveau montage donne
 * un nouvel objet, donc une nouvelle lecture, sans invalidation à écrire.
 *
 * Une lecture qui échoue ou dépasse la borne de qualification est au contraire
 * évincée. « Non qualifié maintenant » reste un refus fermé, mais ne doit pas
 * transformer un gel transitoire du fournisseur en état permanent jusqu'au
 * prochain montage.
 *
 * ## ⚠️ Disponible ne veut pas dire suivi, et suivi ne se déduit pas d'ici
 *
 * `capabilities.get()` publie aussi un `tracked` par opération, mais il ne dit
 * pas ce qui se passe sous une fenêtre `suggesting`. Mesuré le 2026-08-16, dans
 * les deux sens :
 *
 * - `lists.create` est annoncé `tracked:false` et produit **pourtant** une
 *   révision sous cette fenêtre ;
 * - `insert` est annoncé `tracked:true` et n'en produit **aucune** — ni sur le
 *   canal HTML, ni sur le fragment structurel qu'`insert_paragraphs` construit
 *   (`tests/ws-mocked/mesure-suivi-par-verbe.spec.ts`), pendant que `replace` et
 *   `blocks.delete`, annoncés pareil, en produisent une.
 *
 * D'où la règle de #990 : ce champ sert à **refuser** en amont un verbe qui
 * s'annonce non suivable (`operationsNonSuivables`), jamais à **promettre** que
 * le suivi a eu lieu. Cette dernière question se tranche après coup, par la
 * relecture des identités de révision, dans le receipt.
 *
 * ## ⚠️ Une capacité illisible n'est PAS une capacité absente
 *
 * Si `capabilities.get` manque, jette, ou rend une forme inattendue,
 * `lireLesCapacites` rend `null`. Cette valeur n'est pas une liste vide : le
 * document peut savoir écrire, mais Hector ne peut pas le prouver. Les appels
 * d'édition doivent donc la transformer en état `unqualified` et refuser AVANT
 * écriture ; l'inspection, elle, reste autorisée pour montrer le contenu et le
 * diagnostic. Ne jamais replier ce cas sur un catalogue complet : un appel
 * ancien ou forgé atteindrait sinon le fournisseur sans surface qualifiée.
 */

import {
  CANVAS_ACTION_CATALOG,
  estDeclareeATortIndisponible,
  type CanvasActionName,
  type CanvasCapabilityQualification,
} from '@hector/shared-types';
import { EDIT_PLAN_OPERATION_BY_STEP, ETAPES_PLAN_NATIVES_REQUISES } from './editPlanContract';

/** Ce que `capabilities.get()` publie par opération. */
interface EntreeDeCapacite {
  available?: unknown;
  tracked?: unknown;
  dryRun?: unknown;
  reasons?: unknown;
}

export interface CapacitesDuDocument {
  /** Identifiants d'opérations dont `available` vaut explicitement `true`. */
  disponibles: ReadonlySet<string>;
  /**
   * Opérations dont `tracked` vaut explicitement `false` — « ce fournisseur
   * annonce ne pas savoir suivre cette opération » (#990).
   *
   * ⚠️ **Explicitement `false`, jamais « pas `true` »** : une clé absente rend
   * `undefined`, qui dit « je ne sais pas » et non « non ». Les confondre
   * transformerait une lecture incomplète en interdiction.
   */
  nonSuivables: ReadonlySet<string>;
  /**
   * Primitives que le moteur de plans atomiques annonce sous
   * `capabilities.get().planEngine.supportedStepOps` (#1610).
   *
   * Elles ne sont pas des clés de `operations` : les y inventer ferait croire
   * qu'un runtime peut appeler `mutations.apply` dès qu'il sait lister ses
   * blocs. L'ensemble vide signifie seulement que ce moteur n'a pas été
   * annoncé ; les verbes classiques restent qualifiés normalement.
   */
  etapesPlanNatives: ReadonlySet<string>;
  /** Les deux entrypoints du plan sont réellement montés sur ce document. */
  planPreviewDisponible: boolean;
  planApplyDisponible: boolean;
  /** Nombre total d'opérations publiées — sert à repérer une réponse dégénérée. */
  total: number;
}

interface DocAvecCapacites {
  capabilities?: { get?: (entree?: unknown) => unknown };
  mutations?: { preview?: unknown; apply?: unknown };
}

const CACHE = new WeakMap<object, Promise<CapacitesDuDocument | null>>();

function estObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null;
}

/**
 * Some provider namespaces are callable facades with methods on the function
 * object. They are valid method containers, unlike a scalar response.
 */
function estEspaceDeNoms(valeur: unknown): valeur is Record<string, unknown> {
  return estObjet(valeur) || typeof valeur === 'function';
}

/**
 * Retire une lecture devenue inutilisable, sans effacer une tentative plus
 * récente pour le même document.
 *
 * Une première promesse peut rester suspendue après la borne de qualification,
 * pendant qu'un nouvel inspect démarre une seconde lecture. Comparer l'identité
 * de la promesse est donc indispensable : supprimer sans cette garde ferait
 * perdre le résultat de la seconde tentative et recréerait une boucle lente.
 */
function evincerLectureSiCourante(
  doc: unknown,
  lecture: Promise<CapacitesDuDocument | null>,
): void {
  if (estObjet(doc) && CACHE.get(doc) === lecture) {
    CACHE.delete(doc);
  }
}

/**
 * Lit les capacités du document et mémoïse une réponse exploitable par objet
 * `doc`.
 *
 * @returns `null` quand la lecture n'aboutit pas — « je ne sais pas », jamais
 * « rien n'est disponible ». Les frontières d'édition le transforment ensuite
 * explicitement en `unqualified`, elles ne doivent pas le traiter comme une
 * autorisation implicite.
 */
export function lireLesCapacites(
  doc: unknown,
  options: { forcer?: boolean } = {},
): Promise<CapacitesDuDocument | null> {
  if (!estObjet(doc)) return Promise.resolve(null);
  if (!options.forcer) {
    const enCache = CACHE.get(doc);
    if (enCache !== undefined) return enCache;
  }

  const lecture = (async (): Promise<CapacitesDuDocument | null> => {
    const surface = (doc as DocAvecCapacites).capabilities;
    if (typeof surface?.get !== 'function') return null;
    try {
      const brut = await surface.get();
      if (!estObjet(brut)) return null;
      const operations = brut.operations;
      if (!estObjet(operations)) return null;

      const disponibles = new Set<string>();
      const nonSuivables = new Set<string>();
      for (const [id, valeur] of Object.entries(operations)) {
        const entree = estObjet(valeur) ? (valeur as EntreeDeCapacite) : undefined;
        // La seconde branche est une EXCEPTION MESURÉE, pas un assouplissement :
        // `create.image` se déclare `available:false` et pose pourtant les
        // en-têtes en production. Sans elle, un verbe qui la déclare requise
        // serait retiré de `verbesExecutables` — le modèle ne le verrait jamais.
        // La liste, sa mesure et son mécanisme d'auto-retrait vivent dans
        // `OPERATIONS_DECLAREES_A_TORT_INDISPONIBLES` (@hector/shared-types).
        if (entree?.available === true || (entree?.available === false && estDeclareeATortIndisponible(id))) {
          disponibles.add(id);
        }
        if (entree?.tracked === false) nonSuivables.add(id);
      }
      const planEngine = brut.planEngine;
      const supportedStepOps = estObjet(planEngine) && Array.isArray(planEngine.supportedStepOps)
        ? planEngine.supportedStepOps.filter((operation): operation is string => typeof operation === 'string')
        : [];
      const mutations = estEspaceDeNoms(doc) && estEspaceDeNoms(doc.mutations) ? doc.mutations : null;
      return {
        disponibles,
        nonSuivables,
        etapesPlanNatives: new Set(supportedStepOps),
        // `planEngine` énumère les étapes, mais pas les deux méthodes qui les
        // exécutent. Les contrôler ici évite de proposer un verbe que le
        // montage courant refuserait ensuite. Source :
        // https://docs.superdoc.dev/document-api/mutation-plans/ (vérifié 2026-09-19).
        planPreviewDisponible: typeof mutations?.preview === 'function',
        planApplyDisponible: typeof mutations?.apply === 'function',
        total: Object.keys(operations).length,
      };
    } catch {
      // Silencieux à dessein : l'appelant décide quoi en faire, et une lecture
      // ratée ne doit pas remonter comme une erreur d'écriture.
      return null;
    }
  })();

  CACHE.set(doc, lecture);
  // Une lecture rejetée ou illisible n'est pas une vérité stable du document.
  // On la partage avec les appels concurrents déjà en cours, puis le prochain
  // inspect/action peut réessayer sur le même montage. La qualification reste
  // fermée tant qu'aucune de ces tentatives n'a produit de surface prouvée.
  void lecture.then((capacites) => {
    if (capacites === null) evincerLectureSiCourante(doc, lecture);
  });
  return lecture;
}

/**
 * Les opérations requises par `verbe` qui MANQUENT à l'appel.
 *
 * Cette primitive historique reste tolérante pour ses lecteurs locaux. La
 * frontière agent ne l'appelle jamais avec `null` : elle qualifie d'abord le
 * runtime, ce qui rend une rechute fail-open observable dans les tests.
 */
export function operationsManquantes(
  verbe: CanvasActionName,
  capacites: CapacitesDuDocument | null,
): readonly string[] {
  if (capacites === null) return [];
  const requises = CANVAS_ACTION_CATALOG[verbe].requiredOperations;
  return requises.filter((operation) => !capacites.disponibles.has(operation));
}

/**
 * Les opérations d'écriture de `verbe` que le fournisseur annonce **ne pas
 * savoir suivre** (#990).
 *
 * N'interroge que `reviewedOperations` — l'opération par laquelle l'écriture
 * passe — et jamais l'ensemble des opérations requises : `query.match`,
 * `blocks.list` et `trackChanges.list` sont mesurées `tracked:false` parce
 * qu'elles LISENT, et refuser un verbe pour ça couperait tout le produit en
 * régime suivi (voir le TSDoc de `reviewedOperations` dans le catalogue).
 *
 * Cette primitive historique reste tolérante pour ses lecteurs locaux ; toute
 * mutation agent est précédée par `qualifierLesCapacitesDuDocument`.
 */
export function operationsNonSuivables(
  verbe: CanvasActionName,
  capacites: CapacitesDuDocument | null,
): readonly string[] {
  if (capacites === null) return [];
  return CANVAS_ACTION_CATALOG[verbe].reviewedOperations.filter((operation) => capacites.nonSuivables.has(operation));
}

/**
 * Les verbes qui écrivent par le moteur de plans natif, et les étapes dont
 * chacun a besoin. Ces étapes ne sont pas des opérations plates de
 * `capabilities.get()` : elles ne peuvent pas vivre dans `requiredOperations`.
 */
const ETAPES_DE_PLAN_PAR_VERBE: Partial<Record<CanvasActionName, readonly string[]>> = {
  apply_edit_plan: ETAPES_PLAN_NATIVES_REQUISES,
  // `format_text` (#1863) n'emploie que l'étape de forme.
  format_text: [EDIT_PLAN_OPERATION_BY_STEP.format_apply],
  // `format_paragraphs` (#1866) : l'alignement part par la même étape.
  format_paragraphs: [EDIT_PLAN_OPERATION_BY_STEP.format_apply],
  // `copy_format` (#1869) : la forme de texte et l'alignement partent par la même étape.
  copy_format: [EDIT_PLAN_OPERATION_BY_STEP.format_apply],
  // `rewrite_block` : une étape de texte par paragraphe, puis la forme de ce texte.
  rewrite_block: [EDIT_PLAN_OPERATION_BY_STEP.text_rewrite, EDIT_PLAN_OPERATION_BY_STEP.format_apply],
};

/**
 * Ce qui manque au moteur de plans natif pour exécuter `verbe` : ses deux
 * entrées (`mutations.preview`, `mutations.apply`) et les étapes que le verbe
 * emploie. Vide pour un verbe qui n'écrit pas par ce moteur.
 */
export function etapesPlanNativesManquantes(
  verbe: CanvasActionName,
  capacites: CapacitesDuDocument | null,
  docVivant?: unknown,
): readonly string[] {
  const etapesRequises = ETAPES_DE_PLAN_PAR_VERBE[verbe];
  if (capacites === null || etapesRequises === undefined) return [];
  // `capabilities.get()` is stable enough to cache, but the callable mutation
  // facade is mounted later in the SuperDoc lifecycle. A first inspection must
  // not hide a plan-based verb for the rest of a resident document just because
  // `doc.mutations` was not installed yet. Decision paths pass the live `doc`;
  // the cached values remain only a backwards-compatible diagnostic fallback.
  const mutations = docVivant === undefined
    ? null
    : estEspaceDeNoms(docVivant) && estEspaceDeNoms((docVivant as DocAvecCapacites).mutations)
      ? (docVivant as DocAvecCapacites).mutations
      : null;
  const previewDisponible = docVivant === undefined
    ? capacites.planPreviewDisponible
    : typeof mutations?.preview === 'function';
  const applyDisponible = docVivant === undefined
    ? capacites.planApplyDisponible
    : typeof mutations?.apply === 'function';
  return [
    ...(previewDisponible ? [] : ['mutations.preview']),
    ...(applyDisponible ? [] : ['mutations.apply']),
    ...etapesRequises.filter((operation) => !capacites.etapesPlanNatives.has(operation)),
  ];
}

/** Message de refus destiné au modèle quand le régime suivi est inatteignable. */
export function messageDeSuiviImpossible(verbe: CanvasActionName, nonSuivables: readonly string[]): string {
  return (
    `${verbe} a été REFUSÉ avant toute écriture : le suivi des modifications est actif sur ce document, `
    + `et l'éditeur annonce que ${nonSuivables.join(', ')} ne sait pas produire de révision suivie. `
    + 'Rien n\'a été écrit, le document est intact. '
    + 'Deux sorties : demander à l\'avocat s\'il veut couper le suivi pour accepter une écriture directe (sur son '
    + 'accord, set_review_mode enabled:false), ou utiliser un autre verbe. Relancer la même action échouera à l\'identique.'
  );
}

/**
 * Les verbes du catalogue que CE document sait exécuter.
 *
 * C'est cette liste que le snapshot d'inspection publie : le modèle ne doit pas
 * apprendre l'indisponibilité d'un verbe par un refus au tour suivant, il doit
 * ne jamais le voir.
 */
export function verbesExecutables(capacites: CapacitesDuDocument | null, docVivant?: unknown): CanvasActionName[] {
  const noms = Object.keys(CANVAS_ACTION_CATALOG) as CanvasActionName[];
  return noms.filter((nom) => (
    operationsManquantes(nom, capacites).length === 0
    && etapesPlanNativesManquantes(nom, capacites, docVivant).length === 0
  ));
}

/**
 * Délai au bout duquel l'annonce de session part avec une qualification
 * explicite `capabilities_timeout`.
 *
 * Load-bearing : `canvas_document_state` est ce qui fait exister la session
 * canvas pour le backend. Une lecture de capacités qui ne rendrait jamais la
 * main (promesse en suspens côté fournisseur — le `try/catch` de
 * `lireLesCapacites` n'attrape qu'un rejet, pas un gel) retiendrait l'annonce
 * elle-même, et l'agent refuserait d'inspecter un canvas pourtant ouvert. Le
 * pire cas acceptable est donc « annoncé non qualifié » — le modèle garde
 * `canvas_inspect`, mais aucune mutation n'est annoncée — jamais « pas
 * annoncé ».
 */
const DELAI_LECTURE_CAPACITES_MS = 1_500;

/**
 * Qualifie les capacités du document sous borne d'attente.
 *
 * Le résultat est toujours explicite : `qualified` porte les verbes réellement
 * mesurés ; `unqualified` nomme une absence, un rejet ou un délai. La fonction
 * ne modifie jamais le document et sert autant à l'annonce de session qu'à la
 * défense finale de l'exécuteur de mutation.
 */
export async function qualifierLesCapacitesDuDocument(
  doc: unknown,
  options: { delaiMs?: number } = {},
): Promise<CanvasCapabilityQualification> {
  let minuterie: ReturnType<typeof setTimeout> | undefined;
  const borne = new Promise<{ type: 'timeout' }>((resolve) => {
    minuterie = setTimeout(() => resolve({ type: 'timeout' }), options.delaiMs ?? DELAI_LECTURE_CAPACITES_MS);
  });
  try {
    const lecture = lireLesCapacites(doc);
    const resultat = await Promise.race([
      lecture.then((capacites) => ({ type: 'read' as const, capacites })),
      borne,
    ]);
    if (resultat.type === 'timeout') {
      // Le fournisseur peut redevenir disponible sans remontage. Le refus est
      // fermé pour CET appel, mais conserver cette promesse suspendue ferait
      // échouer toutes les qualifications suivantes au même timeout.
      evincerLectureSiCourante(doc, lecture);
      return { status: 'unqualified', reason: 'capabilities_timeout' };
    }
    if (resultat.capacites === null) {
      evincerLectureSiCourante(doc, lecture);
      return { status: 'unqualified', reason: 'capabilities_unreadable' };
    }
    return {
      status: 'qualified',
      executableActions: verbesExecutables(resultat.capacites, doc),
    };
  } finally {
    if (minuterie !== undefined) clearTimeout(minuterie);
  }
}

/**
 * Refus stable quand le runtime ne peut pas être qualifié AVANT une mutation.
 *
 * Le texte écarte les deux mauvaises reprises observées : rejouer une écriture
 * sans preuve, ou remplacer le document pour « réparer » un état de session.
 */
export function messageDeCapacitesNonQualifiees(
  verbe: CanvasActionName,
  reason: Extract<CanvasCapabilityQualification, { status: 'unqualified' }>['reason'],
): string {
  return (
    `${verbe} was REFUSED before any write because the mounted Canvas runtime is not qualified (${reason}). `
    + 'Nothing was written and retrying the same mutation would be blind. Keep the current document in place: '
    + 're-open the Canvas without loading or replacing a document, run canvas_inspect, then continue only once its '
    + 'capability qualification is available.'
  );
}

/** Message de refus destiné au modèle — nommé, et actionnable. */
export function messageDeCapaciteAbsente(verbe: CanvasActionName, manquantes: readonly string[]): string {
  return (
    `${verbe} est indisponible sur ce document : l'éditeur n'expose pas ${manquantes.join(', ')}. `
    + 'Ce n\'est pas un échec passager — relancer la même action échouera à l\'identique. '
    + 'Utilise un autre verbe, ou explique à l\'avocat ce qui ne peut pas être fait ici.'
  );
}
